import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ComicCard from '@/components/base/ComicCard';
import { featuredComics, latestComics, popularComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/siteInfo';

const faqItems = [
  {
    q: '韩漫之家上的漫画都是免费的吗？',
    a: '是的，韩漫之家上所有韩国漫画均免费在线观看。我们致力于为读者提供优质的免费韩漫大全资源，无需付费即可畅享阅读。',
  },
  {
    q: '网站多久更新一次漫画内容？',
    a: '我们每日更新漫画资源，确保读者能够第一时间阅读到最新的章节。热门连载漫画通常与韩国同步更新，追更不落空。',
  },
  {
    q: '如何在韩漫之家找到想看的漫画？',
    a: '您可以通过首页的搜索栏直接搜索漫画名称，也可以按分类浏览（恋爱、动作、奇幻、搞笑、悬疑、治愈、剧情），或查看编辑精选和人气排行榜来发现好看的韩国漫画。',
  },
  {
    q: '支持在手机或平板上观看吗？',
    a: '韩漫之家采用响应式设计，完美适配手机、平板和电脑等各类设备。您可以在任何设备上流畅地在线观看韩国漫画。',
  },
  {
    q: '可以下载漫画吗？',
    a: '目前韩漫之家仅支持在线观看，暂不支持漫画下载功能。但我们提供流畅的在线阅读体验，随时随地都能访问。',
  },
];

const stats = [
  { icon: 'ri-book-open-line', value: '12,800+', label: 'home.statsComics' },
  { icon: 'ri-user-smile-line', value: '52万+', label: 'home.statsVisitors' },
  { icon: 'ri-refresh-line', value: '3,600+', label: 'home.statsUpdates' },
  { icon: 'ri-star-line', value: '4.8/5', label: 'home.statsRating' },
];

export default function HomePage() {
  const { t } = useTranslation();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20artistic%20Korean%20webtoon%20style%20illustration%20background%2C%20colorful%20comic%20panels%20floating%20in%20dreamy%20space%2C%20warm%20orange%20and%20coral%20gradient%2C%20soft%20magical%20atmosphere%2C%20abstract%20manga%20elements%2C%20clean%20modern%20aesthetic%2C%20no%20text%2C%20artistic%20composition&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
            alt="韩漫之家背景"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background-50"></div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white mb-4 leading-tight">
            {t('home.heroTitle')}
            <br />
            <span className="text-primary-300">{t('home.heroTitleHighlight')}</span>
          </h1>
          <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t('home.heroSubtitle')}
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-full transition-all duration-300 text-sm whitespace-nowrap cursor-pointer"
            >
              {t('home.heroCTA')}
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-medium px-6 py-3 rounded-full transition-all duration-300 text-sm whitespace-nowrap cursor-pointer border border-white/30"
            >
              {t('home.heroSecondary')}
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-14 md:mt-20">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-5 border border-white/20"
              >
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-500/20">
                  <i className={`${stat.icon} text-primary-300 text-xl`}></i>
                </div>
                <p className="text-xl md:text-2xl font-bold text-white font-heading">{stat.value}</p>
                <p className="text-xs md:text-sm text-white/60 mt-1">{t(stat.label)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
            {t('home.categoryTitle')}
          </h2>
          <p className="text-sm text-foreground-500">{t('home.categorySubtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer border ${
                cat.slug === 'all'
                  ? 'bg-primary-500 text-white border-primary-500 hover:bg-primary-600'
                  : 'bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300 hover:text-primary-500'
              }`}
            >
              <i className={`${cat.icon} text-base`}></i>
              {cat.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Comics */}
      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-1">
              {t('home.featuredTitle')}
            </h2>
            <p className="text-sm text-foreground-500">{t('home.featuredSubtitle')}</p>
          </div>
          <Link
            to="/category/all"
            className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
          {featuredComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* Latest Updates */}
      <section className="w-full bg-background-100/70 py-10 md:py-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-1">
                {t('home.latestTitle')}
              </h2>
              <p className="text-sm text-foreground-500">{t('home.latestSubtitle')}</p>
            </div>
            <Link
              to="/category/all"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
            {latestComics.slice(0, 10).map((comic) => (
              <ComicCard key={`latest-${comic.id}`} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      {/* Popular Comics */}
      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-1">
              {t('home.popularTitle')}
            </h2>
            <p className="text-sm text-foreground-500">{t('home.popularSubtitle')}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
          {popularComics.slice(0, 10).map((comic, index) => (
            <div key={`popular-${comic.id}`} className="relative">
              <div className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-accent-500 text-white text-xs font-bold flex items-center justify-center z-10 font-heading">
                {index + 1}
              </div>
              <ComicCard comic={comic} />
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full bg-background-100/70 py-10 md:py-14">
        <div className="max-w-[800px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              {t('home.faqTitle')}
            </h2>
            <p className="text-sm text-foreground-500">{t('home.faqSubtitle')}</p>
          </div>
          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-900 pr-4">
                    {item.q}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-300 text-lg shrink-0 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFaqIndex === index && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '韩漫之家',
            url: 'https://www.hanmanzhijia.com',
            description: siteInfo.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.hanmanzhijia.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
            publisher: {
              '@type': 'Organization',
              name: '韩漫之家',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '韩漫之家',
            url: 'https://www.hanmanzhijia.com',
            description: siteInfo.description,
            email: siteInfo.email,
            telephone: siteInfo.phone,
            address: {
              '@type': 'PostalAddress',
              streetAddress: '望京SOHO T2-A座 15层',
              addressLocality: '北京',
              addressRegion: '朝阳区',
              addressCountry: 'CN',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
              },
            })),
          }),
        }}
      />
    </div>
  );
}